import { RefObject, useEffect, useMemo, useState } from 'react'

import { CheckPickerItemType } from './CheckPicker.types'

interface ListedOptionsSource<OptionType extends string> {
  options?: CheckPickerItemType<OptionType>[]
  value?: OptionType[]
  isOpen: boolean
}

interface SelectionCheck<OptionType extends string> {
  option: CheckPickerItemType<OptionType>
  value?: OptionType[]
}

const isListedAsSelected = <OptionType extends string>({
  option,
  value,
}: SelectionCheck<OptionType>) =>
  Boolean(value?.find(selectedValue => selectedValue === option.value))

export const useListedOptions = <OptionType extends string>({
  options,
  value,
  isOpen,
}: ListedOptionsSource<OptionType>) => {
  const [listed, setListed] = useState({ options, value, isOpen })

  if (listed.isOpen !== isOpen || listed.options !== options) {
    setListed({ options, value, isOpen })
  }

  return useMemo(() => {
    const listedOptions = listed.options ?? []

    return {
      selectedOptions: listedOptions.filter(option =>
        isListedAsSelected({ option, value: listed.value }),
      ),
      unselectedOptions: listedOptions.filter(
        option => !isListedAsSelected({ option, value: listed.value }),
      ),
    }
  }, [listed])
}

export const useElementWidth = (elementRef: RefObject<HTMLElement>) => {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const element = elementRef.current
    if (!element) {
      return
    }

    setWidth(element.clientWidth)
    const observer = new ResizeObserver(() => setWidth(element.clientWidth))
    observer.observe(element)

    return () => observer.disconnect()
  }, [elementRef])

  return width
}
